/**
 * Markdown Preview Component
 *
 * Renders markdown content as HTML using react-markdown
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Paper, Text } from '@mantine/core';
import { useMantineColorScheme } from '@mantine/core';
// react-syntax-highlighter: Code blocks inside markdown (fenced ``` blocks) with theme-aware highlighting.
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { MermaidDiagram } from './MermaidDiagram';
import { isContentLink, resolveContentLink } from '../../services/ContentLinkResolver';
import { debugVerboseLog } from '../../utils/debugLog';

export interface MarkdownPreviewProps {
    content: string;
    emptyMessage?: string;
    className?: string;
    style?: React.CSSProperties;
    /** When true, logs anchor navigation decisions to console.debug. */
    debugAnchors?: boolean;
    /**
     * Optional scroll container to use for in-document anchor navigation.
     * When provided, #hash links will scroll this container rather than relying on window scrolling.
     */
    scrollContainerRef?: React.RefObject<HTMLElement | null>;
    /** Called when a resolved Content inter-document link is clicked. */
    onContentLinkClick?: (entityId: string, displayName: string) => void;
}

function getTextFromReactNode(node: any): string {
    if (node == null) return '';
    if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') return String(node);
    if (Array.isArray(node)) return node.map(getTextFromReactNode).join('');
    if (typeof node === 'object' && 'props' in node) {
        return getTextFromReactNode((node as any).props?.children);
    }
    return '';
}

function slugifyHeading(text: string): string {
    return text
        .trim()
        .toLowerCase()
        // GitHub-style: strip punctuation (e.g. "1.2" -> "12"), then hyphenate whitespace.
        .replace(/['"]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/[\s-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 128);
}

function resolveAnchorTarget(id: string, scope: ParentNode): HTMLElement | null {
    const direct =
        document.getElementById(id) ??
        document.getElementById(id.toLowerCase()) ??
        document.getElementById(id.toLowerCase().replace(/\s+/g, '-'));
    if (direct) return direct as HTMLElement;

    // Fallback: TOC may link to base id while renderer dedupes (e.g. "intro-1").
    const safePrefix = id.replace(/"/g, '\\"');
    return (scope.querySelector?.(`[id^="${safePrefix}-"]`) as HTMLElement | null) ?? null;
}

function scrollToAnchorTarget(target: HTMLElement, container: HTMLElement | null, debugAnchors: boolean): void {
    if (container && container.contains(target)) {
        const containerRect = container.getBoundingClientRect();
        const elRect = target.getBoundingClientRect();
        const nextTop = container.scrollTop + (elRect.top - containerRect.top) - 8; // small padding

        if (debugAnchors) {
            debugVerboseLog('🔗 MarkdownPreview scrolling container', {
                containerTag: (container as any)?.tagName,
                containerScrollTopBefore: container.scrollTop,
                containerClientHeight: container.clientHeight,
                containerScrollHeight: container.scrollHeight,
                elTop: elRect.top,
                containerTop: containerRect.top,
                nextTop
            });
        }

        try {
            container.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' as ScrollBehavior });
        } catch {
            // ignore
        }
        container.scrollTop = Math.max(0, nextTop);

        if (debugAnchors) {
            debugVerboseLog('🔗 MarkdownPreview container scrolled', {
                containerScrollTopAfter: container.scrollTop
            });
        }
        return;
    }

    if (debugAnchors) {
        debugVerboseLog('🔗 MarkdownPreview scrolling via scrollIntoView', {
            hasContainer: !!container,
            containerContainsEl: !!(container && container.contains(target))
        });
    }

    try {
        target.scrollIntoView({ block: 'start', behavior: 'smooth' as ScrollBehavior });
    } catch {
        target.scrollIntoView({ block: 'start' });
    }
}

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export const MarkdownPreview: React.FC<MarkdownPreviewProps> = ({
    content,
    emptyMessage = 'No content to display',
    className,
    style,
    debugAnchors = false,
    scrollContainerRef,
    onContentLinkClick
}) => {
    const { colorScheme } = useMantineColorScheme();
    const codeStyle = colorScheme === 'dark' ? vscDarkPlus : vs;
    // Per-render slug counter so heading ids are deterministic even under React 18 StrictMode double render.
    const slugCounts = new Map<string, number>();
    const headingLogCountRef = React.useRef(0);

    const renderHeading = (level: HeadingLevel) => {
        return ({ node: _node, children, ...props }: any) => {
            const text = getTextFromReactNode(children);
            const base = slugifyHeading(text) || 'section';
            const next = (slugCounts.get(base) ?? 0) + 1;
            slugCounts.set(base, next);
            // GitHub-style dedupe: second occurrence becomes "-1", then "-2", etc.
            const id = next === 1 ? base : `${base}-${next - 1}`;

            if (debugAnchors && headingLogCountRef.current < 5) {
                headingLogCountRef.current += 1;
                debugVerboseLog('🔗 MarkdownPreview heading id', { level: `h${level}`, text, id });
            }

            const Tag = (`h${level}` as unknown) as keyof JSX.IntrinsicElements;
            return <Tag id={id} {...props}>{children}</Tag>;
        };
    };

    if (!content || content.trim() === '') {
        return (
            <Box p="sm" ta="center" style={style} className={className}>
                <Text size="sm" c="dimmed">
                    {emptyMessage}
                </Text>
            </Box>
        );
    }

    return (
        <Paper
            p="sm"
            withBorder
            className={className}
            style={style}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    a({ href, children, ...props }: any) {
                        const rawHref = typeof href === 'string' ? href : '';
                        const hasFragment = rawHref.includes('#');
                        return (
                            <a
                                href={rawHref}
                                {...props}
                                onClick={(e) => {
                                    props?.onClick?.(e);
                                    if (debugAnchors) {
                                        debugVerboseLog('🔗 MarkdownPreview link click', {
                                            href: rawHref,
                                            hasFragment,
                                            ctrlKey: e.ctrlKey,
                                            metaKey: e.metaKey,
                                            shiftKey: e.shiftKey,
                                            altKey: e.altKey,
                                        });
                                    }

                                    // Allow modified clicks to behave normally (new tab, etc.)
                                    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

                                    // Intercept Content inter-document links (.md files, bare names)
                                    if (onContentLinkClick && isContentLink(rawHref)) {
                                        e.preventDefault();
                                        const linkEl = e.currentTarget as HTMLAnchorElement;
                                        linkEl.style.opacity = '0.5';
                                        linkEl.style.cursor = 'wait';
                                        resolveContentLink(rawHref)
                                            .then((resolved) => {
                                                linkEl.style.opacity = '';
                                                linkEl.style.cursor = '';
                                                if (resolved) {
                                                    onContentLinkClick(resolved.id, resolved.displayName);
                                                } else {
                                                    linkEl.title = `Document not found: ${rawHref}`;
                                                    linkEl.style.textDecoration = 'line-through';
                                                    setTimeout(() => {
                                                        linkEl.style.textDecoration = '';
                                                        linkEl.title = '';
                                                    }, 2000);
                                                }
                                            })
                                            .catch(() => {
                                                linkEl.style.opacity = '';
                                                linkEl.style.cursor = '';
                                            });
                                        return;
                                    }

                                    if (!hasFragment) return;

                                    // Only intercept "same-page" fragment navigation.
                                    // For hash-routing apps, many internal links may still be same-document anchors.
                                    const fragment = rawHref.split('#').pop() ?? '';
                                    const rawId = decodeURIComponent(fragment);
                                    if (!rawId) {
                                        if (debugAnchors) debugVerboseLog('🔗 MarkdownPreview: empty fragment, skipping');
                                        return;
                                    }

                                    e.preventDefault();
                                    const id = rawId;

                                    if (debugAnchors) {
                                        debugVerboseLog('🔗 MarkdownPreview anchor parsed', {
                                            href: rawHref,
                                            id,
                                            hasScrollContainer: !!scrollContainerRef?.current
                                        });
                                    }

                                    const container = scrollContainerRef?.current;
                                    const scope: ParentNode = container ?? document;
                                    const resolvedEl = resolveAnchorTarget(id, scope);

                                    if (debugAnchors) {
                                        debugVerboseLog('🔗 MarkdownPreview anchor target', {
                                            id,
                                            found: !!resolvedEl,
                                            resolvedId: resolvedEl?.id,
                                            tagName: (resolvedEl as any)?.tagName,
                                            usedPrefixFallback: !!resolvedEl && resolvedEl.id !== id
                                        });
                                    }

                                    if (resolvedEl) {
                                        scrollToAnchorTarget(resolvedEl, container ?? null, debugAnchors);
                                        // Make it focusable for accessibility (without altering tab order).
                                        try {
                                            if (!resolvedEl.hasAttribute('tabindex')) resolvedEl.setAttribute('tabindex', '-1');
                                            (resolvedEl as any).focus?.({ preventScroll: true });
                                        } catch {
                                            // ignore focus errors
                                        }
                                    }
                                    if (debugAnchors && !resolvedEl) {
                                        const headings = Array.from(scope.querySelectorAll?.('h1[id],h2[id],h3[id],h4[id],h5[id],h6[id]') ?? []);
                                        const sample = headings.slice(0, 30).map((h: any) => h.id);
                                        debugVerboseLog('🔗 MarkdownPreview: anchor target not found in DOM', {
                                            id,
                                            headingIdsSample: sample,
                                            totalHeadingsWithId: headings.length,
                                            scopedToContainer: !!container
                                        });
                                    }
                                }}
                            >
                                {children}
                            </a>
                        );
                    },
                    h1: renderHeading(1),
                    h2: renderHeading(2),
                    h3: renderHeading(3),
                    h4: renderHeading(4),
                    h5: renderHeading(5),
                    h6: renderHeading(6),
                    code({ node, inline, className, children, ...props }: any) {
                        const match = /language-(\w+)/.exec(className || '');
                        const language = match ? match[1] : '';
                        if (!inline && language === 'mermaid') {
                            return <MermaidDiagram chart={String(children).replace(/\n$/, '')} />;
                        }
                        return !inline && match ? (
                            <SyntaxHighlighter
                                style={codeStyle}
                                language={language}
                                PreTag="div"
                                {...props}
                            >
                                {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                        ) : (
                            <code className={className} {...props}>
                                {children}
                            </code>
                        );
                    }
                }}
            >
                {content}
            </ReactMarkdown>
        </Paper>
    );
};
